/**
 * Heat Master オーナー登録フォームスクリプト。
 * Shopify Forms アプリ（Shadow DOM 内）の以下を制御する:
 * - 「ご購入商品」をドロップダウンで選択し、フォームフィールドへ反映
 * - 「製造番号（ロット）」をドロップダウンで選択。ロットは商品カテゴリごとに実在する
 *   ものが異なる (例: PREMIUM は F のみ) ため、選択肢は選ばれた商品のカテゴリで絞り込む
 *   (商品未選択の間は disabled)。対応表は section 経由で lotOptions として渡る
 * - **複数商品の同時登録** (+追加 ボタンで N ペアを 1 回の送信で登録)
 * - 「都道府県」を 47 都道府県のドロップダウンに置換 (owner#prefecture の
 *   text 入力 → select。namespace 由来の「オーナーズ - 」ラベルも整える)
 *
 * 複数商品の送信フォーマット (顧客判断 2026-05-20):
 *   - 単一テキストの Forms フィールドにパイプ `|` 区切りで詰める
 *   - 例: product_latest = "HM-001|HM-002|HM-003"
 *         product_lot_number_latest = "A|B|C"
 *   - Shopify Flow 側で `|` で split + zip して product_log に複数行追記する想定
 *   - 不完全なペア (片方のみ選択) は集計から除外
 *
 * data-heat-master-register → オーナー登録フォーム用 JSON
 */
(function () {
  'use strict';

  var FIELD_EMAIL   = 'email';
  var FIELD_PRODUCT = 'heat_master#product_latest';
  var FIELD_LOT     = 'heat_master#product_lot_number_latest';

  // ロット (製造番号) の全候補。商品カテゴリごとに実在するロットは異なるため、通常は
  // lotMap (section の lot_options 設定 / heat_master.lot_options メタフィールド由来) で
  // 絞り込む。該当カテゴリの記載が無い場合のみこの全候補へフォールバックする。
  var LOT_OPTIONS = ['A','B','C','D','E','F','G','H','I','J','K','L'];
  // 洗濯表示タグが読めない顧客の救済経路。カテゴリ別の絞り込み結果にも常に付与する。
  var LOT_UNKNOWN = '不明';

  // 都道府県マスタは JS では保持しない。snippets/prefecture-select-options.liquid を
  // 唯一の供給源とし、section が as:'json' で配列を data-heat-master-register へ渡す
  // (rData.prefectures)。

  var FIELD_MAIL_MAGAZINE = 'customer#mail_magazine';
  var FIELD_DM            = 'customer#dm_opt_in';

  // 複数商品設定
  var MAX_PAIRS = 10;          // 1 回の送信で登録できる上限ペア数
  var PAIR_DELIMITER = '|';    // 単一フィールドへの詰め込み区切り (Flow 側 split key)

  var SELECT_STYLE = [
    'display:block',
    'width:100%',
    'padding:var(--space-primitive-8) var(--space-primitive-12)',
    // iOS Safari のフォーカス時自動ズーム防止のため、フォーム入力要素は最低 16px
    // (styleguide「フォーム入力フィールドの font-size」)。商品/ロット/都道府県の
    // 全 select がこの style を共有する。
    'font-size:var(--font-size-16)',
    'font-family:inherit',
    'border:1px solid var(--color-border-neutral-default)',
    'border-radius:var(--space-primitive-4)',
    'background:var(--color-surface-neutral-default)',
    'color:inherit',
    'margin-bottom:0'
  ].join(';');

  // ---- 共通ユーティリティ ----

  function getShadowRoot() {
    var embed = document.querySelector('shopify-forms-embed');
    return embed ? embed.shadowRoot : null;
  }

  // textarea は id なし・name 属性のみのケースあり（Shopify Forms の仕様）
  function getField(shadow, fieldId) {
    return shadow.getElementById(fieldId)
      || shadow.querySelector('[aria-labelledby="label-' + fieldId + '"]')
      || shadow.querySelector('[name="' + fieldId + '"]');
  }

  function getFieldSection(shadow, fieldId) {
    var field = getField(shadow, fieldId);
    if (!field) return null;
    var label = shadow.getElementById('label-' + fieldId);
    if (label && label.parentElement && label.parentElement.contains(field)) {
      return label.parentElement;
    }
    return field.parentElement;
  }

  function setNativeValue(el, value) {
    var proto = el.tagName === 'SELECT'
      ? HTMLSelectElement.prototype
      : el.tagName === 'TEXTAREA'
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
    var setter = Object.getOwnPropertyDescriptor(proto, 'value');
    if (setter && setter.set) setter.set.call(el, value);
    else el.value = value;
    el.dispatchEvent(new Event('input',  { bubbles: true, composed: true }));
    el.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    el.dispatchEvent(new Event('blur',   { bubbles: true, composed: true }));
  }

  function hideFieldSection(shadow, fieldId) {
    var section = getFieldSection(shadow, fieldId);
    if (section) section.style.display = 'none';
  }

  function makeReadonly(field) {
    field.readOnly = true;
    field.setAttribute('aria-readonly', 'true');
    field.style.pointerEvents = 'none';
  }

  function buildSelect(placeholder, selectId) {
    var select = document.createElement('select');
    select.id = selectId;
    select.style.cssText = SELECT_STYLE;
    var ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholder;
    select.appendChild(ph);
    return select;
  }

  // select を <label> + <select> の縦列 (flex col) にまとめる。
  // wrap した時にも各 select の用途 (商品 / ロット) を視覚的に判別可能にする。
  function buildSelectCol(labelText, select, flexCss) {
    var col = document.createElement('div');
    col.style.cssText = 'display:flex;flex-direction:column;' + flexCss;
    var lbl = document.createElement('label');
    lbl.htmlFor = select.id;
    lbl.textContent = labelText;
    lbl.style.cssText = 'display:block;font-size:var(--font-size-12);font-weight:var(--font-weight-semibold);color:var(--color-text-neutral-secondary);margin-bottom:var(--space-primitive-4);';
    col.appendChild(lbl);
    col.appendChild(select);
    return col;
  }

  function insertDropdown(shadow, fieldId, labelText, select) {
    var section = getFieldSection(shadow, fieldId);
    if (!section || !section.parentElement) return;
    var wrapper = document.createElement('div');
    wrapper.style.marginBottom = 'var(--space-primitive-20)';
    var lbl = document.createElement('label');
    lbl.htmlFor = select.id;
    lbl.textContent = labelText;
    lbl.style.cssText = 'display:block;font-size:var(--font-size-14);margin-bottom:var(--space-primitive-8);font-weight:var(--font-weight-semibold);';
    wrapper.appendChild(lbl);
    wrapper.appendChild(select);
    section.parentElement.insertBefore(wrapper, section);
  }

  // "カテゴリ>商品名" 形式を optgroup で階層化
  function buildProductSelect(placeholder, idPrefix, products) {
    var select = buildSelect(placeholder, idPrefix + '-product');
    var groups = {};
    var groupOrder = [];
    products.forEach(function (name) {
      if (typeof name !== 'string') return;
      var sep = name.indexOf('>');
      var group     = sep !== -1 ? name.slice(0, sep).trim() : '';
      var itemLabel = sep !== -1 ? name.slice(sep + 1).trim() : name;
      if (!groups[group]) { groups[group] = []; groupOrder.push(group); }
      groups[group].push({ label: itemLabel, value: name });
    });
    groupOrder.forEach(function (group) {
      var container = select;
      if (group) {
        container = document.createElement('optgroup');
        container.label = group;
      }
      groups[group].forEach(function (item) {
        var opt = document.createElement('option');
        opt.value = item.value;
        opt.textContent = item.label;
        container.appendChild(opt);
      });
      if (group) select.appendChild(container);
    });
    return select;
  }

  // "カテゴリ>A,B,C" 形式の行を { カテゴリ: ['A','B','C'] } へ変換する。
  // カテゴリ名は admin 入力のため、"constructor" 等 Object.prototype のキーと衝突しない
  // よう prototype なしのオブジェクトへ格納する。
  function parseLotMap(lines) {
    var map = Object.create(null);
    if (!Array.isArray(lines)) return map;
    lines.forEach(function (line) {
      if (typeof line !== 'string') return;
      var sep = line.indexOf('>');
      if (sep === -1) return;
      var category = line.slice(0, sep).trim();
      var lots = line.slice(sep + 1).split(',').map(function (lot) {
        return lot.trim();
      }).filter(Boolean);
      if (category && lots.length) map[category] = lots;
    });
    return map;
  }

  // 商品値 ("カテゴリ>商品名") に対して選択可能なロット一覧を返す。
  // 商品未選択なら空配列 (= ロット select を disabled にする合図)。
  function lotOptionsFor(productValue, lotMap) {
    if (!productValue) return [];
    var sep = productValue.indexOf('>');
    var category = sep !== -1 ? productValue.slice(0, sep).trim() : '';
    var lots = category ? lotMap[category] : null;
    var out = lots ? lots.slice() : LOT_OPTIONS.slice();
    if (out.indexOf(LOT_UNKNOWN) === -1) out.push(LOT_UNKNOWN);
    return out;
  }

  // 商品未選択の間は選択肢なし + disabled。商品が選ばれてから該当カテゴリの
  // ロットだけを出す (「商品選択後にロットを選ぶ」導線)。
  function buildLotSelect(placeholder, idPrefix) {
    var select = buildSelect(placeholder, idPrefix + '-lot');
    select.disabled = true;
    return select;
  }

  // 商品選択に追随してロット select の option を差し替える。
  // 差し替えは placeholder 以降を truncate → DocumentFragment で一括追加し、
  // option ごとの DOM 追加を避ける (styleguide「不要な再描画・大量DOM操作を避ける」)。
  function renderLotOptions(lotSelect, productValue, lotMap) {
    var previous = lotSelect.value;
    lotSelect.length = 1; // placeholder は残す
    var lots = lotOptionsFor(productValue, lotMap);
    var fragment = document.createDocumentFragment();
    lots.forEach(function (val) {
      var opt = document.createElement('option');
      opt.value = val;
      opt.textContent = val;
      fragment.appendChild(opt);
    });
    lotSelect.appendChild(fragment);
    // 直前の選択が新カテゴリにも在れば維持し、無ければ未選択へ戻す (例: Heatech の A を
    // 選んだ後に PREMIUM へ変更 → A は PREMIUM に存在しないのでクリア)
    lotSelect.value = lots.indexOf(previous) !== -1 ? previous : '';
    lotSelect.disabled = lots.length === 0;
  }

  function waitForFields(fieldIds, callback) {
    var maxWait = 10000;
    var interval = 200;
    var elapsed = 0;

    function check() {
      var shadow = getShadowRoot();
      if (shadow && fieldIds.every(function (id) { return !!getField(shadow, id); })) {
        callback(shadow);
        return;
      }
      elapsed += interval;
      if (elapsed < maxWait) {
        setTimeout(check, interval);
      } else {
        console.warn('[heat-master-owner-form] フォームフィールドが見つかりませんでした:', fieldIds);
      }
    }

    check();
  }

  function parseLabels(raw) {
    var defaults = {
      product:      'ご購入商品を選択',
      lot:          '製造番号（ロット）を選択',
      placeholder:  '選択してください',
      prefecture:   '都道府県',
      hint:         '複数商品を一度に登録できます (最大 {max} 件)',
      addButton:    '+ 商品を追加',
      removeButton: '行を削除'
    };
    if (!raw) return defaults;
    return {
      product:      raw.product      || defaults.product,
      lot:          raw.lot          || defaults.lot,
      placeholder:  raw.placeholder  || defaults.placeholder,
      prefecture:   raw.prefecture   || defaults.prefecture,
      hint:         raw.hint         || defaults.hint,
      addButton:    raw.addButton    || defaults.addButton,
      removeButton: raw.removeButton || defaults.removeButton,
      lotImage:     raw.lotImage     || ''
    };
  }

  // ---- 複数商品対応: 1 つのコンテナに N 個の (商品, ロット) ペアを表示 ----
  //
  // UI 構造:
  //   <div container>
  //     <header>ご購入商品 / 製造番号 (最大 N)</header>
  //     <div pairsContainer>
  //       <div pairRow>[商品 select][ロット select][× remove]</div>
  //       <div pairRow>...</div>
  //     </div>
  //     <button + 商品を追加 />
  //   </div>
  //
  // 元の Forms app の単一 input は readonly + hidden 化し、change のたびに
  // 集計値 (`|` 区切り) を setNativeValue で書き込む。
  function attachDropdowns(shadow, labels, idPrefix, products, lotMap) {
    var productField = getField(shadow, FIELD_PRODUCT);
    var lotField     = getField(shadow, FIELD_LOT);
    if (!productField || !lotField) return;

    makeReadonly(productField);
    makeReadonly(lotField);
    hideFieldSection(shadow, FIELD_PRODUCT);
    hideFieldSection(shadow, FIELD_LOT);

    var productSection = getFieldSection(shadow, FIELD_PRODUCT);
    if (!productSection || !productSection.parentElement) return;

    var pairs = []; // { row, productSelect, lotSelect, removeBtn? }

    // ---- container DOM 構築 ----
    var container = document.createElement('div');
    container.style.cssText = 'margin-bottom:var(--space-primitive-20);';

    // ② 購入都度登録の赤字案内は section (custom-heat-master-owner-register.liquid) の
    //    ヘッダー (サブタイトル直下) に移設したため、フォーム内では生成しない。

    var header = document.createElement('div');
    header.style.cssText = 'font-size:var(--font-size-14);font-weight:var(--font-weight-semibold);margin-bottom:var(--space-primitive-8);';
    header.textContent = labels.product + ' / ' + labels.lot;
    container.appendChild(header);

    var hint = document.createElement('div');
    hint.style.cssText = 'font-size:var(--font-size-12);color:var(--color-text-neutral-secondary);margin-bottom:var(--space-primitive-12);';
    // Liquid 側で {max} を MAX_PAIRS に置換 (i18n / 管理画面編集可能化 R5 指摘)
    hint.textContent = labels.hint.replace('{max}', MAX_PAIRS);
    container.appendChild(hint);

    // ① ロット（製造番号）確認の案内
    var lotTagNote = document.createElement('p');
    lotTagNote.style.cssText = [
      'font-size:var(--font-size-12)',
      'color:var(--color-text-neutral-secondary)',
      'margin-bottom:var(--space-primitive-12)'
    ].join(';');
    lotTagNote.textContent = labels.lotTagNote || '※製品番号(ロット)は、製品の内側の洗濯表示タグのアルファベットをご確認ください。';
    container.appendChild(lotTagNote);

    // ロット位置の確認用ガイド画像 (品質表示タグ)。labels.lotImage は section の asset_url。
    if (labels.lotImage) {
      var lotImg = document.createElement('img');
      lotImg.src = labels.lotImage;
      lotImg.alt = 'ロット番号(製造番号)の確認位置 — 製品内側の品質表示タグ';
      lotImg.loading = 'lazy';
      lotImg.style.cssText = [
        'display:block',
        'width:100%',
        'max-width:280px',
        'height:auto',
        'border:1px solid var(--color-border-neutral-default)',
        'border-radius:var(--space-primitive-4)',
        'margin-bottom:var(--space-primitive-12)'
      ].join(';');
      container.appendChild(lotImg);
    }

    var pairsContainer = document.createElement('div');
    container.appendChild(pairsContainer);

    var addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.textContent = labels.addButton;
    addBtn.style.cssText = [
      'display:inline-block',
      'margin-top:var(--space-primitive-8)',
      'padding:var(--space-primitive-8) var(--space-primitive-16)',
      'font-size:var(--font-size-14)',
      'font-family:inherit',
      'border:1px solid var(--color-border-neutral-default)',
      'border-radius:9999px',
      'background:var(--color-surface-neutral-default)',
      'color:inherit',
      'cursor:pointer'
    ].join(';');
    container.appendChild(addBtn);

    productSection.parentElement.insertBefore(container, productSection);

    // ---- ペア行の構築 + aggregation ----
    function aggregate() {
      var complete = pairs.filter(function (p) {
        return p.productSelect.value && p.lotSelect.value;
      });
      setNativeValue(productField, complete.map(function (p) { return p.productSelect.value; }).join(PAIR_DELIMITER));
      setNativeValue(lotField,     complete.map(function (p) { return p.lotSelect.value;     }).join(PAIR_DELIMITER));
    }

    function updateAddBtnState() {
      addBtn.disabled = pairs.length >= MAX_PAIRS;
      addBtn.style.opacity = addBtn.disabled ? '0.5' : '1';
      addBtn.style.cursor  = addBtn.disabled ? 'not-allowed' : 'pointer';
    }

    function buildPairRow(showRemove) {
      var rowId = idPrefix + '-row-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
      var row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:var(--space-primitive-8);align-items:flex-end;margin-bottom:var(--space-primitive-12);flex-wrap:wrap;';

      var productSelect = buildProductSelect(labels.placeholder, rowId, products);
      // a11y: <label htmlFor> を付けるが、視覚ラベルなしでも識別できるよう
      // aria-label もフォールバックとして残す (#210 R4 指摘)
      productSelect.setAttribute('aria-label', labels.product);

      var lotSelect = buildLotSelect(labels.placeholder, rowId);
      lotSelect.setAttribute('aria-label', labels.lot);

      // 商品が変わるたびに、そのカテゴリで実在するロットだけへ選択肢を絞り直す。
      productSelect.addEventListener('change', function () {
        renderLotOptions(lotSelect, productSelect.value, lotMap);
        aggregate(); // ロットがクリアされた場合、そのペアは不完全として集計から外す
      });
      lotSelect.addEventListener('change', aggregate);

      // 各 select を col 化 (label + select) して、コンテナ幅が狭く wrap した
      // 際にも「どちらが商品でどちらがロットか」が常に視認できるようにする。
      // ※ flex/min-width は col 側 (= row の直接の子) に集約する。
      //   select 自体に flex:1 1 20rem を残すと、parent が flex-direction:column
      //   の中で flex-basis が高さとして効き、select が 200px に伸びるため (#211 R1 HIGH 指摘)。
      //
      // 商品セレクト幅: 20rem = 200px ハードコード。
      //   商品名は "カテゴリ>商品名" 形式で長く、--space-primitive-* (最大 12.8rem)
      //   では足りない。商品セレクト専用幅トークンは未定義のため rem 値で明示。
      // ロットセレクト幅: --space-primitive-128 = 12.8rem (= 128px) でトークン化 (#211 R2 指摘)。
      row.appendChild(buildSelectCol(labels.product, productSelect, 'flex:1 1 20rem;min-width:20rem;'));
      row.appendChild(buildSelectCol(labels.lot,     lotSelect,     'flex:0 0 auto;min-width:var(--space-primitive-128);'));

      var removeBtn = null;
      if (showRemove) {
        removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.textContent = '×';
        removeBtn.setAttribute('aria-label', labels.removeButton);
        removeBtn.style.cssText = [
          'flex:0 0 auto',
          'width:var(--space-primitive-32)',
          'height:var(--space-primitive-32)',
          'font-size:var(--font-size-16)',
          'line-height:1',
          'border:1px solid var(--color-border-neutral-default)',
          'border-radius:9999px',
          'background:var(--color-surface-neutral-default)',
          'color:inherit',
          'cursor:pointer'
        ].join(';');
        removeBtn.addEventListener('click', function () {
          removePair(row);
        });
        row.appendChild(removeBtn);
      }

      return { row: row, productSelect: productSelect, lotSelect: lotSelect, removeBtn: removeBtn };
    }

    function addPair() {
      if (pairs.length >= MAX_PAIRS) return;
      // 1 行目以外は remove ボタン付き (常に最低 1 行は残す)
      var showRemove = pairs.length > 0;
      var pair = buildPairRow(showRemove);
      pairs.push(pair);
      pairsContainer.appendChild(pair.row);
      updateAddBtnState();
    }

    function removePair(row) {
      var idx = -1;
      for (var i = 0; i < pairs.length; i++) {
        if (pairs[i].row === row) { idx = i; break; }
      }
      if (idx === -1) return;

      // a11y: 削除前にフォーカス遷移先を確定 (R4 指摘)
      //   - 前の行があればその row の remove ボタン (= 同じ操作系)
      //   - 前の行が無ければ「+ 商品を追加」ボタン
      // 行 remove で document.activeElement が body に飛ぶのを防ぎ、
      // キーボード操作の文脈を保つ。
      var nextFocus = idx > 0
        ? (pairs[idx - 1].removeBtn || pairs[idx - 1].productSelect)
        : addBtn;

      pairs.splice(idx, 1);
      row.remove();
      aggregate();
      updateAddBtnState();

      if (nextFocus && typeof nextFocus.focus === 'function') {
        // disabled な要素 (例: max 到達後の addBtn) には focus できないので
        // try/catch でガード。失敗時は何もしない (body にフォールバック)。
        try { nextFocus.focus(); } catch (e) {}
      }
    }

    addBtn.addEventListener('click', addPair);

    // 初期: 1 行 (remove なし)
    addPair();
  }

  // ---- 都道府県セレクト (owner#prefecture) ----
  //
  // Shopify Forms は owner メタフィールドを type=text の入力としてレンダーし、
  // ラベルも namespace 由来の「オーナーズ - 都道府県」になる。これを:
  //   (a) 47 都道府県の <select> に差し替え (自由入力の表記ゆれを排除)
  //   (b) ラベルを「都道府県」に整え namespace prefix「オーナーズ - 」を非表示
  // にする。元の text 入力は readonly + hidden 化し、select の値を書き戻して送信する
  // (opt-in ラジオ / 商品ドロップダウンと同じ「元フィールドへ値を集約」パターン)。
  //
  // prefectures: 都道府県名の配列 (section が prefecture-select-options.liquid から
  // as:'json' で生成して渡す)。空ならフィールドを変えず skip する。
  function insertPrefectureDropdown(shadow, fieldId, labels, prefectures, initialValue) {
    if (!Array.isArray(prefectures) || prefectures.length === 0) return; // データ無しは skip
    var field = getField(shadow, fieldId);
    if (!field) return; // フィールドが無ければ何もしない (サイレント skip)

    // required は可視 select へ移譲してから元フィールドを hidden 化する。
    // hidden かつ required のまま残すとネイティブバリデーションが focusable でない
    // 要素で発火し submit がサイレントブロックされる (insertOptInGroup と同じ理由)。
    var isRequired = field.required;
    field.required = false;
    makeReadonly(field);
    hideFieldSection(shadow, fieldId);

    var select = buildSelect(labels.placeholder, 'hm-reg-prefecture');
    prefectures.forEach(function (pref) {
      var opt = document.createElement('option');
      opt.value = pref;
      opt.textContent = pref;
      select.appendChild(opt);
    });
    if (isRequired) select.required = true;
    // aria-label は付けない。insertDropdown が <label htmlFor> を生成して select と
    // 紐付けるため冗長で、SR の二重読み上げを招く (#339 R1 指摘)。

    // prefill: 一致する option があれば初期選択し、元フィールドへ書き戻す。
    // 一致しない (一覧外 / 英語表記等) 場合は select を未選択にし、hidden に残った
    // 無効値も空へクリアして「表示(未選択)」と「送信データ」を一致させる (#339 R2 指摘)。
    // ただし field.value が既に空なら何もしない (prefill 無しの clean load で空値の
    // blur を発火させ、必須バリデーションを早期誤発火させるのを避ける)。
    var optionExists = false;
    if (initialValue) {
      optionExists = Array.prototype.some.call(select.options, function (opt) {
        return opt.value === initialValue;
      });
    }
    if (optionExists) {
      select.value = initialValue;
      setNativeValue(field, initialValue);
    } else {
      select.value = '';
      if (field.value) setNativeValue(field, '');
    }

    select.addEventListener('change', function () {
      setNativeValue(field, select.value); // select → 元フィールド
    });

    insertDropdown(shadow, fieldId, labels.prefecture, select);
  }

  // ---- opt-in ラジオグループの挿入 ----
  // afterFieldId のセクションの直後に、ラジオボタングループを差し込む。
  // shadow DOM 内の既存テキストフィールド (fieldId) が存在すれば変更時に値を書き込む。
  function insertOptInGroup(shadow, afterFieldId, fieldId, groupLabel, yesText, noText) {
    var afterSection = getFieldSection(shadow, afterFieldId);
    if (!afterSection || !afterSection.parentElement) return;

    // 元の Shopify Forms フィールドは非表示にし、required はカスタムラジオへ移譲する。
    // 非表示のまま required を残すと、ブラウザのネイティブバリデーションが focusable
    // でない要素で発火して submit がサイレントブロックされる ("An invalid form control
    // ... is not focusable.")。また残置すると Shopify 生成の入力欄とラジオが二重表示になる。
    var hidden = getField(shadow, fieldId);
    if (!hidden) {
      console.warn('[heat-master-owner-form] フィールドが見つかりません:', fieldId);
      return;
    }
    var isRequired = hidden.required;
    hidden.required = false;
    makeReadonly(hidden);
    hideFieldSection(shadow, fieldId);

    var wrapper = document.createElement('div');
    wrapper.style.cssText = 'margin-bottom:var(--space-primitive-20);';

    var legend = document.createElement('p');
    legend.style.cssText = [
      'font-size:var(--font-size-14)',
      'font-weight:var(--font-weight-semibold)',
      'margin-bottom:var(--space-primitive-8)'
    ].join(';');
    legend.textContent = groupLabel;
    // a11y: ラジオ群をグループとして読み上げさせるため legend に id を付与し radiogroup の名前にする
    legend.id = 'optin-legend-' + fieldId.replace(/[^a-zA-Z0-9_-]/g, '-');
    wrapper.appendChild(legend);

    var radioRow = document.createElement('div');
    radioRow.style.cssText = 'display:flex;gap:var(--space-primitive-20);';
    radioRow.setAttribute('role', 'radiogroup');
    radioRow.setAttribute('aria-labelledby', legend.id);

    var radios = [];
    [{ value: 'yes', text: yesText }, { value: 'no', text: noText }].forEach(function (opt) {
      // fieldId は "customer#mail_magazine" 等。id に "#" 等が混ざると querySelector や
      // ラベル関連付けで壊れるため、id 安全な文字以外を "-" に置換する (name は別途維持)。
      var radioId = fieldId.replace(/[^a-zA-Z0-9_-]/g, '-') + '-' + opt.value;
      var lbl = document.createElement('label');
      lbl.htmlFor = radioId;
      lbl.style.cssText = [
        'display:flex',
        'align-items:center',
        'gap:var(--space-primitive-8)',
        'cursor:pointer',
        'font-size:var(--font-size-14)'
      ].join(';');
      var radio = document.createElement('input');
      radio.type  = 'radio';
      radio.id    = radioId;
      radio.name  = fieldId + '-radio';
      radio.value = opt.value;
      if (isRequired) radio.required = true; // 元フィールドの required を引き継ぐ
      // 初期同期: 既に prefill 済みの hidden 値があればラジオの checked を合わせる
      if (hidden.value === opt.value) radio.checked = true;
      radio.addEventListener('change', function () {
        setNativeValue(hidden, opt.value); // ラジオ → hidden
      });
      radios.push(radio);
      lbl.appendChild(radio);
      lbl.appendChild(document.createTextNode(opt.text));
      radioRow.appendChild(lbl);
    });

    // hidden → ラジオ の追従同期。Shopify Forms / prefill が後から hidden に値を入れても
    // ラジオの checked が変わらないと「どちらも未選択」に見える (双方向同期バグ)。hidden の
    // change / input を監視して checked を追従させる。setNativeValue は input/change 両方を
    // 発火するため両方を購読。プログラム的な .checked 設定は change を発火しないのでループしない。
    function syncRadiosFromHidden() {
      radios.forEach(function (r) { r.checked = (r.value === hidden.value); });
    }
    hidden.addEventListener('change', syncRadiosFromHidden);
    hidden.addEventListener('input', syncRadiosFromHidden);

    wrapper.appendChild(radioRow);
    afterSection.parentElement.insertBefore(wrapper, afterSection.nextSibling);
  }

  // SELECT は対応 option がロード済みの場合のみ set。text/textarea/date は値が違えば set
  function setIfNeeded(field, value) {
    if (!field || !value) return;
    if (field.tagName === 'SELECT') {
      var matchedValue = null;
      for (var i = 0; i < field.options.length; i++) {
        var opt = field.options[i];
        if (opt.value === value || opt.textContent.trim() === value) {
          matchedValue = opt.value;
          break;
        }
      }
      if (matchedValue === null) return; // option 未ロード — 次回リトライで拾う
      if (field.value !== matchedValue) setNativeValue(field, matchedValue);
    } else {
      if (field.value !== value) setNativeValue(field, value);
    }
  }

  // 生年月日 — Shopify Forms は MM/DD/YYYY の 3 入力 (aria-labelledby="label-MM" 等)
  function fillBirthday(shadow, ymd) {
    if (!ymd) return;
    var parts = ymd.split('-');
    if (parts.length !== 3) return;
    var inputs = shadow.querySelectorAll('input[aria-labelledby]');
    inputs.forEach(function (inp) {
      var labelId = inp.getAttribute('aria-labelledby');
      if (labelId === 'label-YYYY' && inp.value !== parts[0]) setNativeValue(inp, parts[0]);
      if (labelId === 'label-MM'   && inp.value !== parts[1]) setNativeValue(inp, parts[1]);
      if (labelId === 'label-DD'   && inp.value !== parts[2]) setNativeValue(inp, parts[2]);
    });
  }

  // Shopify Forms カスタム dropdown (button + lazy render panel) の prefill。
  // 構造:
  //   <button aria-controls="{fieldId}-selector-dropdown">
  //     <span class="_selectToggleText_*">表示テキスト</span>
  //   </button>
  //   <!-- 開いた時のみ DOM に出現 -->
  //   <div id="{fieldId}-selector-dropdown">
  //     <div role="option" id="select-{fieldId}-{value}">{value}</div>
  //     ...
  //   </div>
  // hidden input が無く値は component state のみのため、click を 2 回連鎖して set する。
  var dropdownInFlight = {};
  var dropdownFailed = {};
  function fillCustomDropdown(shadow, fieldId, value) {
    if (!value) return;
    if (dropdownInFlight[fieldId] || dropdownFailed[fieldId]) return;
    var btn = shadow.querySelector('button[aria-controls="' + fieldId + '-selector-dropdown"]');
    if (!btn) return;

    var textSpan = btn.querySelector('[class*="selectToggleText"]');
    if (textSpan && textSpan.textContent.trim() === value) return; // 既に一致

    dropdownInFlight[fieldId] = true;
    btn.click(); // panel を render させる
    setTimeout(function () {
      var panel = shadow.getElementById(fieldId + '-selector-dropdown');
      if (panel) {
        var options = panel.querySelectorAll('[role="option"]');
        var matched = false;
        for (var i = 0; i < options.length; i++) {
          if (options[i].textContent.trim() === value) {
            options[i].click();
            matched = true;
            break;
          }
        }
        // 一致 option なし → 値が選択肢に存在しない。リトライ無意味なので失敗マーク
        if (!matched) {
          dropdownFailed[fieldId] = true;
          if (btn.getAttribute('aria-expanded') === 'true') btn.click(); // 閉じる
        }
      }
      dropdownInFlight[fieldId] = false;
    }, 100);
  }

  function applyAllPrefill(shadow, c) {
    var fieldMap = {
      'email':                     c.email,
      'last_name':                 c.lastName,
      'first_name':                c.firstName,
      'customer#family_name_kana': c.familyNameKana,
      'customer#first_name_kana':  c.firstNameKana,
      'customer#phone':            c.phone,
      'owner#postal_code':         c.postalCode,
      'owner#address1':            c.address1,
      'owner#address2':            c.address2,
      'owner#address3':            c.address3
    };
    Object.keys(fieldMap).forEach(function (fieldId) {
      setIfNeeded(getField(shadow, fieldId), fieldMap[fieldId]);
    });

    // 性別は Shopify Forms 独自のカスタム dropdown。
    // 都道府県 (owner#prefecture) は insertPrefectureDropdown が独自 select に置換 +
    // prefill するため、ここでは扱わない。
    fillCustomDropdown(shadow, 'customer#gender', c.gender);

    // 生年月日は MM/DD/YYYY 3 入力構成
    fillBirthday(shadow, c.birthday);
  }

  // 5秒間ポーリングして prefill を再適用する。
  // - 標準フィールド (last_name / first_name) はレンダリング後に空文字で上書きされるため
  // - カスタム dropdown も polling 経由で button 出現を待つ
  function prefillCustomerFields(shadow, c) {
    applyAllPrefill(shadow, c);
    var elapsed = 0;
    var interval = 200;
    var maxWait = 5000;
    var timer = setInterval(function () {
      elapsed += interval;
      applyAllPrefill(shadow, c);
      if (elapsed >= maxWait) clearInterval(timer);
    }, interval);

    hideFieldSection(shadow, FIELD_EMAIL);
  }

  // ---- オーナー登録フォーム ----

  var registerEl = document.querySelector('[data-heat-master-register]');
  if (registerEl) {
    var rData = { customer: null, products: [], lotOptions: [], prefectures: [], labels: parseLabels(null) };
    try {
      var rAttr = registerEl.getAttribute('data-heat-master-register');
      var rp    = rAttr ? JSON.parse(rAttr) : null;
      if (rp) {
        rData.customer    = rp.customer || null;
        rData.products    = Array.isArray(rp.products) ? rp.products : [];
        rData.lotOptions  = Array.isArray(rp.lotOptions) ? rp.lotOptions : [];
        rData.prefectures = Array.isArray(rp.prefectures) ? rp.prefectures : [];
        rData.labels      = parseLabels(rp.labels);
      }
    } catch (e) {}

    waitForFields([FIELD_EMAIL, FIELD_PRODUCT, FIELD_LOT], function (shadow) {
      if (rData.customer && rData.customer.email) {
        prefillCustomerFields(shadow, rData.customer);
      }
      attachDropdowns(shadow, rData.labels, 'hm-reg', rData.products, parseLotMap(rData.lotOptions));

      // 都道府県 (owner#prefecture) を 47 都道府県の select に差し替え。
      // 都道府県マスタは section が prefecture-select-options.liquid から渡す。
      // ログイン顧客は metafield 由来の prefecture を初期選択する。
      insertPrefectureDropdown(
        shadow,
        'owner#prefecture',
        rData.labels,
        rData.prefectures,
        rData.customer ? rData.customer.prefecture : ''
      );

      // ③ メールアドレス下: メールマガジン opt-in
      insertOptInGroup(
        shadow,
        FIELD_EMAIL,
        FIELD_MAIL_MAGAZINE,
        rData.labels.mailMagazine || 'メールマガジン',
        rData.labels.mailMagazineYes || '受け取る',
        rData.labels.mailMagazineNo || '受け取らない'
      );

      // ③ 住所下: DM opt-in（address3 = 住所の最終フィールド）
      insertOptInGroup(
        shadow,
        'owner#address3',
        FIELD_DM,
        rData.labels.dm || 'DM',
        rData.labels.dmYes || 'DMを受け取る',
        rData.labels.dmNo || 'DMを受け取らない'
      );
    });
  }

})();
